import { DatePickerProps, TimePrecision } from '@blueprintjs/datetime';
import { getLocalDate } from 'app/helpers/getLocalDate';
import { useLocalTimeCallback } from 'app/hooks/use-local-time';
import { useAppSelector } from 'app/redux/store';
import { useI18n } from 'core/hooks/useI18n';
import { i18nInstance } from 'i18n';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import { ButtonBar, Cancel, Confirm, DatePickerWrapper, StyledDatePicker } from './StyledComponents';
import { loadDateFnsLocale } from '../loadDateFnsLocale';
import { DEFAULT_LOCALE } from 'app/components/date-selectors/dateTimeSelect.constants';

type IProps = {
  initialValue: number | null;
  onValueChange: (millis: number | null) => void;
  onCancel: () => void;
  dateOnly?: boolean;
  customPresets?: React.ReactNode;
  maxDate?: DateTime;
} & Omit<DatePickerProps, 'maxDate'>;

export const DateTimeSelector = (props: IProps) => {
  const { initialValue, onValueChange, onCancel, dateOnly, customPresets, onChange, maxDate, ...datePickerProps } =
    props;
  const { l10n } = useI18n(['app/components', 'app']);
  const timePickerProps = dateOnly ? undefined : { useAmPm: true };
  const localTime = useLocalTimeCallback();
  const { ianaTimezone } = useAppSelector(state => state.profile.facility);

  const [date, setDate] = useState<Date | null>(() => {
    if (initialValue === null || initialValue === undefined) {
      return getLocalDate(localTime());
    }
    return getLocalDate(localTime(initialValue));
  });

  const handleDateChange = useCallback((selectedDate: Date | null) => {
    setDate(selectedDate);
  }, []);

  const handleOkClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    if (date === null) return onValueChange(null);
    onValueChange(DateTime.fromJSDate(date).setZone(ianaTimezone, { keepLocalTime: true }).toMillis());
  };

  const handleCancelClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    event.stopPropagation();
    onCancel();
  };

  const localMaxDate = useMemo(() => {
    return maxDate && getLocalDate(maxDate);
  }, [maxDate]);

  return (
    <DatePickerWrapper>
      <StyledDatePicker
        timePrecision={dateOnly ? undefined : TimePrecision.MINUTE}
        timePickerProps={timePickerProps}
        defaultValue={date ?? undefined}
        onChange={handleDateChange}
        dayPickerProps={{ weekStartsOn: 1 }}
        locale={i18nInstance.language ?? DEFAULT_LOCALE}
        dateFnsLocaleLoader={loadDateFnsLocale}
        maxDate={localMaxDate}
        {...datePickerProps}
      />
      {customPresets}
      <ButtonBar>
        <Confirm text={l10n('app:action.confirm')} onClick={handleOkClick} />
        <Cancel text={l10n('app:action.cancel')} onClick={handleCancelClick} />
      </ButtonBar>
    </DatePickerWrapper>
  );
};
